import React, { useEffect, useRef } from 'react';

import { translations } from '../translations/en';
import { STEP_NAMES } from '../types/HearingTestTypes';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  currentStep,
  totalSteps,
}) => {
  const chunk1End = STEP_NAMES.SOUND_CHECK;
  const chunk2End = STEP_NAMES.RIGHT_4000HZ;
  const chunk3End = STEP_NAMES.RESULTS;

  const getChunkProgress = (chunkStart: number, chunkEnd: number): number => {
    if (currentStep <= chunkStart) return 0;
    if (currentStep >= chunkEnd) return 100;
    return ((currentStep - chunkStart) / (chunkEnd - chunkStart)) * 100;
  };

  const chunk1Progress = getChunkProgress(0, chunk1End);
  const chunk2Progress = getChunkProgress(chunk1End, chunk2End);
  const chunk3Progress = getChunkProgress(chunk2End, chunk3End);

  const getOverallProgress = (): number => {
    return Math.round((currentStep / (totalSteps - 1)) * 100);
  };

  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (progressRef.current) {
      progressRef.current.focus();
    }
  }, [currentStep]);

  return (
    <section
      className="mb-4 outline-none"
      ref={progressRef}
      tabIndex={-1}
      aria-label="Test progress"
    >
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {translations.stepof
          .replace('{current}', (currentStep + 1).toString())
          .replace('{total}', totalSteps.toString())}
        . {getOverallProgress()}% complete.
      </div>

      <div className="flex gap-4" role="presentation" aria-hidden="true">
        <div className="flex-1">
          <div className="mb-2 text-sm font-medium text-gray-700">{translations.aboutyou}</div>
          <div className="h-2 rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-300"
              style={{ width: `${chunk1Progress}%` }}
            ></div>
          </div>
        </div>
        <div className="flex-1">
          <div className="mb-2 text-center text-sm font-medium text-gray-700">
            {translations.hearingtest}
          </div>
          <div className="h-2 rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-300"
              style={{ width: `${chunk2Progress}%` }}
            ></div>
          </div>
        </div>
        <div className="flex-1">
          <div className="mb-2 text-right text-sm font-medium text-gray-700">
            {translations.results}
          </div>
          <div className="h-2 rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-300"
              style={{ width: `${chunk3Progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
};
